import React, { useEffect, useState, useRef } from 'react';
import { message } from '../Message';
import axios from 'axios';
import Spinner from '../Spinner';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import CompareImage from '../CompareImage';
import IconButton from '@mui/material/IconButton';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

const modes = {
  pro: 'Pro',
  basic: 'Basic',
};

const APP_URL = process.env.REACT_APP_APP_URL;
const REOK = process.env.REACT_APP_REOK;

const Customers = ({ t, isCreating, firebase, onFetchUser, userData }) => {
  const scrollableRef = useRef(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("");
  const [email, setEmail] = useState("");
  const [removeId, setRemoveId] = useState("");
  const [openCompare, setOpenCompare] = useState(false);
  const [compareImages, setCompareImages] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [trialForm, setTrialForm] = useState({
    pro: true,
    proCredits: 0,
    modelsAllowed: 0,
    headshotsTrial: ['basic', 'pro', 'executive']
  });

  const HEADSHOT_TYPES = {
    BASIC: 'basic',
    PRO: 'pro',
    EXECUTIVE: 'executive'
  };

  const columns = [
    { id: 'email', label: 'Email', minWidth: 50 },
    { id: 'refCode', label: 'Ref', minWidth: 50 },
    // { id: 'paid', label: 'Paid', minWidth: 50 },
    { id: 'createdAt', label: 'Created At', minWidth: 100 },
    { id: 'actions', label: 'Trial', minWidth: 50 }, // New column
  ];

  useEffect(() => {
    firebase.getCustomers()
      .then((result) => {
        const jobsArray = result.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

        const updatedData = jobsArray.map((item) => {
          const date = new Date(item.joinedDate);
          const gmtOffset = 7; // GMT+7
          date.setHours(date.getHours() + gmtOffset);
          const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date.toISOString().substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

          const isPaid = item.trial ? "No" : "Yes"

          return {
            ...item,
            createdAt: formattedDate,
            paid: isPaid
          };
        });

        setRows(updatedData);
      })
      .catch((error) => {
        console.log(error);
      });

    return () => {
    };
  }, []);

  const filteredRows = rows.filter(row =>
    row.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClose = () => {
    setOpen(false);
    setMode("");
  };

  const handleOk = async () => {
    setIsSpinning(true);

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${firebase.auth.currentUser.accessToken}`,
    };

    const requestDataEng = {
      id: removeId,
      reok: REOK,
    };

    if (mode === "approve") {
      try {
        const result = await axios.post(`${APP_URL}/approve-renew`, requestDataEng, { headers });
        message.success(result.data);
        const updatedData = rows.filter(item => item.id !== removeId);
        setRows(updatedData);
      } catch (error) {
        message.error(error.response.data);
      }
    }

    if (mode === "disapprove") {
      try {
        const result = await axios.post(`${APP_URL}/disapprove-renew`, requestDataEng, { headers });
        message.success(result.data);
        const updatedData = rows.filter(item => item.id !== removeId);
        setRows(updatedData);
      } catch (error) {
        message.error(error.response.data);
      }
    }

    handleClose();
    setIsSpinning(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const onCloseCompare = () => {
    setOpenCompare(false);
  }

  const handleOpenTrialModal = (user) => {
    setSelectedUser(user);
    setTrialForm({
      pro: true,
      proCredits: user.proCredits || 0,
      modelsAllowed: user.modelsAllowed || 0,
      headshotsTrial: user.headshotsTrial || []
    });
    setOpenTrialModal(true);
  };

  const handleCloseTrialModal = () => {
    setOpenTrialModal(false);
    setSelectedUser(null);
  };

  const handleTrialFormChange = (field) => (event) => {
    setTrialForm(prev => ({
      ...prev,
      [field]: field === 'pro' ? event.target.checked : Number(event.target.value)
    }));
  };

  const handleTrialSubmit = async () => {
    try {
      await firebase.updateFreeTrial(selectedUser.id, trialForm);
      message.success('Trial updated successfully');

      // Update the local state
      setRows(prev => prev.map(row =>
        row.id === selectedUser.id
          ? { ...row, ...trialForm }
          : row
      ));

      handleCloseTrialModal();
    } catch (error) {
      message.error(error.response?.data || 'Failed to update trial');
    }
  };

  return (
    <>
      <div className="my-design" ref={scrollableRef}>
        <h3>Customers</h3>

        <TextField
          fullWidth
          label="Search by email"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ marginBottom: '1rem' }}
        />

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: "100%" }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                        {columns.map((column) => {
                          if (column.id === 'actions') {
                            return (
                              <TableCell key={column.id}>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleOpenTrialModal(row)}
                                  title="Manage Trial"
                                >
                                  <CardGiftcardIcon />
                                </IconButton>
                              </TableCell>
                            );
                          }
                          const value = row[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.format && typeof value === 'number'
                                ? column.format(value)
                                : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Attention please ⚠⚠⚠</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {mode === "approve" && (
              <>
                Your are excuting <span style={{ color: "green" }}>{mode}</span> to <span style={{ color: "green" }}>{email}</span>
              </>
            )}
            {mode === "disapprove" && (
              <>
                Your are excuting <span style={{ color: "red" }}>{mode}</span> to <span style={{ color: "red" }}>{email}</span>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleOk} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTrialModal}
        onClose={handleCloseTrialModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Trial for {selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <TextField
              fullWidth
              label="Pro Credits"
              type="number"
              value={trialForm.proCredits}
              onChange={handleTrialFormChange('proCredits')}
            />

            <TextField
              fullWidth
              label="Models Allowed"
              type="number"
              value={trialForm.modelsAllowed}
              onChange={handleTrialFormChange('modelsAllowed')}
            />

            <FormControl fullWidth>
              <InputLabel>Headshots</InputLabel>
              <Select
                multiple
                value={trialForm.headshotsTrial}
                label="Headshots"
                onChange={(event) => {
                  setTrialForm(prev => ({
                    ...prev,
                    headshotsTrial: event.target.value
                  }));
                }}
              >
                <MenuItem value={HEADSHOT_TYPES.BASIC}>Basic</MenuItem>
                <MenuItem value={HEADSHOT_TYPES.PRO}>Pro</MenuItem>
                <MenuItem value={HEADSHOT_TYPES.EXECUTIVE}>Executive</MenuItem>
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTrialModal}>Cancel</Button>
          <Button onClick={handleTrialSubmit} variant="contained" color="primary">
            Update Trial
          </Button>
        </DialogActions>
      </Dialog>

      {openCompare && <CompareImage onClose={onCloseCompare} images={compareImages} />}
      {isSpinning && <Spinner />}
    </>
  );
};

export default Customers;